import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';

import React from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { Text, TextType } from 'ui-components/components/text';
import { TOOL_TIP_POSITIONS } from 'ui-components/services/tooltip-service';
import { useGlobalModalLaunchContext } from '../contexts/modal-context/global-modal-context';

import { messages } from './messages';
import style from './style.module.less';

/**
 * @typedef {import('../prop-types/intl-types').MessageDescriptor} MessageDescriptor
 * @typedef {import('ui-components/services/tooltip-service-types.d.ts').TooltipPositionValues} TooltipPositionValues
 */
/**
 * A component that displays a lock icon with a tooltip indicating a feature is not available on the current plan.
 * Used to visually indicate locked functionality throughout the application.
 * @param {object} props - The component props
 * @param {MessageDescriptor} [props.messageDescriptor] - Message descriptor object to display as disabled text next to the lock icon
 * @param {string} [props.iconClass] - Additional CSS class to apply to the lock icon for custom styling
 * @param {MessageDescriptor} [props.tooltipMessageDescriptor] - Custom message descriptor for the tooltip text
 * @param {TooltipPositionValues} [props.position] - Position of the tooltip relative to the icon
 * @param {boolean} [props.isClickable] - Whether the tooltip content is clickable
 * @returns {JSX.Element} A div containing the lock icon with tooltip
 */
export const LockIconWithTooltip = ({
  messageDescriptor = undefined,
  iconClass = undefined,
  position = TOOL_TIP_POSITIONS.bottom,
  tooltipMessageDescriptor = messages.featureNotAvailableOnCurrentPlan, // Default to existing message
  isClickable = false,
}) => {
  const { openManageSubscriptionDrawer } = useGlobalModalLaunchContext();

  return (
    <div className={style['feature-gate-container']} onClick={openManageSubscriptionDrawer}>
      {messageDescriptor && (
        <Text className={style['feature-gate-disabled-text']} textType={TextType.Paragraph} value={messageDescriptor} />
      )}
      <Tooltip position={position}>
        <div>
          <FontAwesomeIcon
            className={classnames(style['feature-gate-icon'], { [style['clickable-icon']]: isClickable }, iconClass)}
            icon={faLock}
          />
        </div>
        <TooltipContent isClickable={isClickable}>
          <FormattedMessage {...tooltipMessageDescriptor} />
        </TooltipContent>
      </Tooltip>
    </div>
  );
};
